import { Textarea } from "@/src/components/ui/textarea";
import { useToast } from "@/src/components/ui/use-toast";
import { useFetch } from "@/src/lib/useFetch";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import { useLoaderData, useNavigate } from "react-router-dom";
import { PatchCountryValues, PatchCountrySchema } from "../schemas";
import { Label } from "@/src/components/ui/label";
import { RelationSingleInput } from "../components/RelationSingleInput";
import { SuccessResponse, fetch } from "@/src/lib/fetch";
import { Country, TopLevelRegion } from "../model";
import { Button } from "@/src/components/ui/button";
import { extractFromMultiName } from "../utils";
import { Yacht } from "../../Yachts/model";
import { YachtsSection } from "../components/YachtsSection";
import { FormCheckbox } from "@/src/components/ui/form-checkbox";
import { FormErrors } from "../components/FormErrors";

export async function countryLoader({ params }: { params: { countryId: string } }) {
  const response = await fetch<SuccessResponse<Country>>(`/api/locations/countries/${params.countryId}`);
  return { defaultValues: response.data, countryId: params.countryId };
}

export function EditCountry() {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { defaultValues, countryId } = useLoaderData() as {
    defaultValues: Partial<PatchCountryValues>;
    countryId: string;
  };

  const methods = useForm<PatchCountryValues>({
    resolver: zodResolver(PatchCountrySchema),
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const loadTlr = async (q: string) => {
    const data = await fetch<{ data: TopLevelRegion[] }>(
      `/api/locations/top-level-regions?page=1&pageSize=100&filter=${encodeURIComponent(q)}`,
    );
    return data.data;
  };

  const loadYachts = async () => {
    const data = await fetch<{ data: Yacht[] }>(`/api/locations/countries/${countryId}/yachts`);
    return data.data;
  };

  const onSubmit = async (data: PatchCountryValues) => {
    try {
      await fetch(`/api/locations/countries/${countryId}`, "PATCH", data);
      toast({ title: "Success", description: "Country updated successfully" });
      navigate(`/locations/countries/${countryId}`);
    } catch (error) {
      toast({ title: "Error", description: "Failed to update country", variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this country?")) {
      try {
        await fetch(`/api/locations/countries/${countryId}`, "DELETE");
        toast({ title: "Success", description: "Country deleted successfully" });
        navigate("/locations/countries");
      } catch (error) {
        toast({ title: "Error", description: "Failed to delete country", variant: "destructive" });
      }
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-2xl shadow-md bg-white p-4">
        <h2 className="text-lg font-semibold">Edit Country - {extractFromMultiName(defaultValues.names)}</h2>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-4">
            <div>
              <Label>Names</Label>
              <Textarea {...register("names")} placeholder="Enter names, one per line" />
            </div>

            <div>
              <Label>Code</Label>
              <Textarea {...register("code")} placeholder="Country code" />
            </div>

            <RelationSingleInput name="tlr" label="TLR" loadOptions={loadTlr} />

            <FormErrors errors={errors} />

            <Button type="submit">Save</Button>
            <Button variant="destructive" type="button" onClick={handleDelete} className="ml-2">
              Delete
            </Button>
          </form>
        </FormProvider>
      </div>
      <YachtsSection loadYachts={loadYachts} />
    </div>
  );
}
